<template>
    <app-layout>
        <Head>
            <title>Anfrage einstellen</title>
        </Head>

        <template v-slot:overline>
            <translate>Anfrage einstellen</translate>
        </template>

        <template v-slot:headline>{{ this.$page.props.user.name }}</template>

        <template v-slot:stepper>
            <stepper>
                <step icon="list-basket-checkmark" class-names="active" routing="ask-for-quote.1">
                    <translate>Bauteilinformationen</translate>
                </step>
                <step icon="delivery-shipment-packages-setting" class-names="active" routing="ask-for-quote.2">
                    <translate>Lieferung</translate>
                </step>
                <step icon="credit-card-security-protection">
                    <translate>Anfrage senden</translate>
                </step>
            </stepper>
        </template>

        <div class="ask-for-quote">
            <card>
                <card-content>
                    <h5 v-translate>Zusammenfassung</h5>
                    <form @submit.prevent="submit">
                        <div class="ask-for-quote--elements">
                            <card minify="1" outline="1" class="ask-for-quote--element"
                                  v-for="(askForQuote) in askForQuoteCards" :key="askForQuote.index">
                                <card-header>
                                    <card-subtitle>{{ askForQuote?.project_name }}</card-subtitle>
                                </card-header>
                                <card-content class="ask-for-quote--content">
                                    <div class="thumbnail">
                                        <img :src="askForQuote.sketch_file_preview" alt="Bauteil">
                                    </div>
                                    <div class="description">
                                        <description-group>
                                            <description-element :caption="askForQuote.lotqty" :title="this.$gettext('Stückzahl')"/>
                                            <description-element :caption="askForQuote.delivery_date"
                                                                 :title="this.$gettext('Wunschliefertermin')" is-date="true"/>
                                            <description-element :caption="askForQuote.sketch_file_info?.original_name"
                                                                 :title="this.$gettext('Bauteilzeichnung')"/>
                                            <description-element :caption="askForQuote.cad_file_info?.original_name"
                                                                 :title="this.$gettext('3D-Daten')"/>
                                        </description-group>
                                    </div>
                                </card-content>
                            </card>
                        </div>
                        <div class="ask-for-quote--grid">
                            <div class="ask-for-quote--left">
                                <card minify="1" outline="1">
                                    <card-header>
                                        <card-overline><translate>Lieferadresse</translate></card-overline>
                                    </card-header>
                                    <card-content>
                                        <p>{{ this?.askForQuoteAddress?.company }}<br/>
                                            {{ this?.askForQuoteAddress?.name }}<br/>
                                            {{ this?.askForQuoteAddress?.street }}
                                            {{ this?.askForQuoteAddress?.street_no }}<br/>
                                            {{ this?.askForQuoteAddress?.zip }}
                                            {{ this?.askForQuoteAddress?.city }}<br/>
                                            {{ this?.askForQuoteCountry?.country_name }}
                                        </p>
                                        <p v-if="this?.askForQuoteAddress?.phone">Telefon:
                                            {{ this?.askForQuoteAddress?.phone }}</p>
                                    </card-content>
                                </card>
                            </div>
                            <div class="ask-for-quote--right">
                                <form-checkbox id="terms_1" v-model:checked="form.terms_1" :error="form.errors.terms_1"
                                               :option="termsOption"/>
                                <form-checkbox id="terms_2" v-model:checked="form.terms_2" :error="form.errors.terms_2"
                                               :option="this.$gettext('Ich bestätige, dass das/die gewünschte(n) Bauteil(e) nicht in Waffen-, Kraft-, Luft-, Schienen- oder Wasserfahrzeuge eingebaut wird/werden.')"/>
                                <div class="form-actions">
                                    <form-button type="button" icon="button-arrow-left-circle" @click="back()"
                                                 icon-before="1">
                                        <translate>zurück</translate>
                                    </form-button>
                                    <form-button type="submit" color="secondary" icon="button-arrow-right-circle" :disabled="form.processing" :class="{'btn--processing': form.processing}">
                                        <translate>weiter zum nächsten Schritt</translate>
                                    </form-button>
                                </div>
                            </div>
                        </div>
                    </form>
                </card-content>
            </card>
        </div>
    </app-layout>
</template>

<script>
import AppLayout from '@partfactory/Layouts/AppLayout';
import {useForm} from '@inertiajs/vue3';
import Card from '@partfactory/Shared/Card/Card';
import CardContent from '@partfactory/Shared/Card/CardContent';
import FormButton from '@partfactory/Shared/Form/FormButton';
import Stepper from '@partfactory/Shared/Stepper/Stepper';
import Step from '@partfactory/Shared/Stepper/Step';
import CardHeader from '@partfactory/Shared/Card/CardHeader';
import CardOverline from '@partfactory/Shared/Card/CardOverline';
import CardSubtitle from '@partfactory/Shared/Card/CardSubtitle';
import FormCheckbox from '@partfactory/Shared/Form/FormCheckbox';
import DescriptionGroup from '@partfactory/Shared/Elements/DescriptionGroup';
import DescriptionElement from '@partfactory/Shared/Elements/DescriptionElement';

export default {
    name: 'AskForQuote.3',
    components: {
        DescriptionElement,
        DescriptionGroup,
        FormCheckbox,
        CardSubtitle,
        CardOverline,
        CardHeader,
        Stepper,
        Step,
        FormButton,
        CardContent,
        Card,
        AppLayout,
    },
    props: {
        askForQuotes: Object,
        sketchFiles: Object,
        cadFiles: Object,
        askForQuoteAddress: Object,
        askForQuoteCountry: Object,
    },
    data() {
        const form = useForm({
            terms_1: null,
            terms_2: null,
        });

        return {form};
    },
    computed: {
        askForQuoteCards() {
            return this.askForQuotes.map((askForQuote, index) => {
                let lotqty = [];
                lotqty.push(askForQuote?.lotqty_1 + ' ' + this.$gettext('Stück'));
                askForQuote?.lotqty_2 ? lotqty.push(askForQuote?.lotqty_2 + ' ' + this.$gettext('Stück')) : '';
                askForQuote?.lotqty_3 ? lotqty.push(askForQuote?.lotqty_3 + ' ' + this.$gettext('Stück')) : '';
                askForQuote?.lotqty_4 ? lotqty.push(askForQuote?.lotqty_4 + ' ' + this.$gettext('Stück')) : '';

                askForQuote['lotqty'] = lotqty.filter(lot => lot !== null).join(', ');

                const file_informations = this?.sketchFiles?.[index].file_informations;
                askForQuote['sketch_file_preview'] = file_informations.thumbnail ? '/sketch-file/serve/client/' + index + '?' + Date.now() : '';

                askForQuote['sketch_file_info'] = file_informations;

                askForQuote['cad_file_info'] = this?.cadFiles?.[index]?.file_informations;

                return askForQuote;
            });
        },
        termsOption() {
            const disclaimer_url = this.$language.current === 'en' ? this.$page.props.urls.disclaimer_en: this.$page.props.urls.disclaimer_de;
            const termsandconditions_url = this.$language.current === 'en' ? this.$page.props.urls.termsandconditions_en: this.$page.props.urls.termsandconditions_de;

            let translated = this.$gettext('Ich akzeptiere die <a href="%{disclaimer}" target="_blank">Datenschutzerklärung</a> und <a href="%{termsandconditions}" target="_blank">AGB</a>.');
            return this.$gettextInterpolate(translated, { disclaimer: disclaimer_url, termsandconditions: termsandconditions_url });
        }
    },
    methods: {
        submit() {
            this.form
                .transform(data => ({
                    ...data,
                    terms_1: this.form.terms_1 ? 'true' : '',
                    terms_2: this.form.terms_2 ? 'true' : '',
                    _token: this.$page.props.csrf_token,
                }))
                .post(this.$route('client.ask-for-quote.3.post'), {
                    onSuccess: () => {
                        // Anfrage-Formular abgeschickt (Schritt 3)
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                            'event': 'form_send',
                            'formName': 'Anfrage',
                            'formStep': '3 - Anfrage einstellen, Anfrage abgeschlossen',
                            'formLang': this.$language.current,
                        });
                    }
                });
        },
        back() {
            this.$inertia.visit(this.$$route('ask-for-quote.2'));
        }
    }
};
</script>
